import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { MegaMenuNav } from "@/components/MegaMenuNav";
import { Footer } from "@/components/Footer";
import { CheckCircle2, ArrowRight, CheckSquare, Bell, TrendingUp } from "lucide-react";
import { Helmet } from "react-helmet-async";

export default function ApplicationTrackerPage() {
  return (
    <>
      <Helmet>
        <title>Application Tracker - Manage All Your Job Applications | LoopBaan.ai</title>
        <meta name="description" content="Track all your job applications in one dashboard. Get status updates, follow-up reminders, and analytics on your job search." />
      </Helmet>

      <div className="min-h-screen bg-background">
        <MegaMenuNav />

        <section className="relative overflow-hidden py-20 md:py-32 bg-gradient-to-br from-primary/10 via-background to-accent/10">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-6 bg-primary/20 text-primary border-primary/30">ORGANIZATION</Badge>
              <h1 className="mb-6 text-5xl md:text-7xl font-bold leading-tight">
                Never Lose Track of{" "}
                <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  An Application
                </span>
              </h1>
              <p className="mb-8 text-xl text-muted-foreground max-w-2xl mx-auto">
                Manage all your job applications in one organized dashboard. Track status, set reminders, and analyze your job search performance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/signup">
                  <Button size="lg" className="gap-2">
                    Get Organized <ArrowRight className="h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid gap-8 md:grid-cols-3 max-w-6xl mx-auto">
              {[
                {
                  icon: CheckSquare,
                  title: "Status Tracking",
                  description: "Track every application from submitted to offer. Know exactly where each opportunity stands."
                },
                {
                  icon: Bell,
                  title: "Smart Reminders",
                  description: "Get automatic reminders to follow up with recruiters at the perfect time. Never miss an opportunity."
                },
                {
                  icon: TrendingUp,
                  title: "Analytics & Insights",
                  description: "See response rates, interview success, and identify what's working in your job search."
                }
              ].map((feature, i) => (
                <Card key={i} className="p-6 hover:shadow-lg transition-all">
                  <feature.icon className="h-12 w-12 text-primary mb-4" />
                  <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-bold text-center mb-12">Stay Organized</h2>
              <div className="grid gap-6 md:grid-cols-2">
                {[
                  "Track applications across all platforms",
                  "Automatic status updates",
                  "Follow-up reminders",
                  "Interview scheduling",
                  "Notes and contacts for each application",
                  "Response rate analytics",
                  "Email integration",
                  "Export to spreadsheet"
                ].map((benefit, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <span className="text-lg">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Take Control of Your Job Search
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Never lose track of an application again. Start organizing your job search today.
            </p>
            <Link to="/signup">
              <Button size="lg" className="gap-2">
                Start Tracking <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
